import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useConsent } from '../hooks/useConsent';
import { useConsentScript } from '../hooks/useConsentScript';
import { CookieCategory } from '../types';

/**
 * Google Analytics 4, vincolato al consenso di PROFILAZIONE.
 *
 * GA4 NON è un cookie "tecnico/anonimizzato" ai fini del Garante (trasferimento
 * dati verso Google, identificazione del client) → si carica SOLO con consenso
 * esplicito di profilazione e viene disattivato alla revoca. Niente snippet in
 * index.html (caricherebbe GA prima della scelta dell'utente).
 *
 * ID via env VITE_GA_MEASUREMENT_ID, con fallback all'ID di produzione.
 */
const GA_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string) || 'G-RB1JH1C2MT';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function clearGaCookies() {
  try {
    const domain = window.location.hostname;
    document.cookie.split(';').forEach((c) => {
      const name = (c.split('=')[0] ?? '').trim();
      if (name.startsWith('_ga')) {
        const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
        document.cookie = `${name}=;${expire}`;
        document.cookie = `${name}=;${expire};domain=.${domain}`;
      }
    });
  } catch {
    /* best-effort */
  }
}

export function GoogleAnalytics() {
  const granted = useConsent(CookieCategory.PROFILING);
  const location = useLocation();
  const [gtagReady, setGtagReady] = useState(false);

  useConsentScript(CookieCategory.PROFILING, {
    id: 'ga4-gtag',
    src: `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`,
    onLoad: () => {
      (window as unknown as Record<string, boolean>)[`ga-disable-${GA_ID}`] = false;
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer.push(args);
      };
      window.gtag('js', new Date());
      // page_view inviato manualmente (SPA) nell'effect sotto.
      window.gtag('config', GA_ID, { anonymize_ip: true, send_page_view: false });
      setGtagReady(true);
    },
    onRevoke: () => {
      setGtagReady(false);
      (window as unknown as Record<string, boolean>)[`ga-disable-${GA_ID}`] = true;
      clearGaCookies();
    },
  });

  // page_view iniziale e a ogni cambio route (solo con consenso e gtag pronto).
  useEffect(() => {
    if (!granted || !gtagReady || !window.gtag) return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [granted, gtagReady, location.pathname, location.search]);

  return null;
}
